import { GAME_SETTINGS, ROUND_TITLE_BY_ROUND_TYPE, SPEED_SETTINGS } from "app/data/game-settings";
import type { WordData } from "app/game-data/useFetchGameData";
import { RoundType } from "app/game-data/useFetchGameData";
import useGameData from "app/game-data/useGameData";
import { useGameState } from "app/game/useGame";
import getWordCorrectness from "app/score/getWordCorrectness";
import { ScoreAction } from "app/score/scoreState";
import { useScoreDispatch, useScoreState } from "app/score/useScore";
import PauseOverlay from "app/speed/PauseOverlay";
import { SpeedProvider } from "app/speed/SpeedProvider";
import { SpeedAction } from "app/speed/speedState";
import useCurrentScore from "app/speed/useCurrentScore";
import { useSpeedActionDispatch, useSpeedState } from "app/speed/useSpeed";
import KeyboardGroup from "app/spelling-input/KeyboardGroup";
import Header from "app/ui/Header";
import MenuButton from "app/ui/MenuButton";
import NextRoundButton from "app/ui/NextRoundButton";
import ProgressBar from "app/ui/ProgressBar";
import RoundIntroScreen from "app/ui/RoundIntroScreen";
import WordClues, { type WordCluesHandle } from "app/ui/WordClues";
import WordSpellingRecord from "app/ui/WordSpellingRecord";
import cn from "app/utils/classnames";
import useAnimatedScorePopup from "app/utils/useAnimatedScorePopup";
import { useInterval } from "app/utils/useInterval";
import usePreloadedAudio from "app/utils/usePreloadedAudio";
import useWindowSize, { WindowSize } from "app/utils/useWindowSize";
import mixpanel from "mixpanel-browser";
import { useEffectEvent, useRef, useState } from "react";
import { useKey } from "react-use";

interface SpeedRoundProps {
  words: Array<WordData>;
  category: string;
  roundIndex: number;
  onComplete: () => void;
  className?: string;
}

function SpeedRound({
  words,
  category,
  roundIndex,
  onComplete,
  className,
}: SpeedRoundProps) {
  const timerDuration = SPEED_SETTINGS.timePerWord * words.length;
  const { inputType } = useGameState();
  const gameData = useGameData();
  const roundState = useSpeedState();
  const roundDispatch = useSpeedActionDispatch();
  const scoreDispatch = useScoreDispatch();
  const scoreState = useScoreState();
  const [isStarted, setIsStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);
  const { windowWidth, windowHeight } = useWindowSize();
  const wordCluesRef = useRef<WordCluesHandle>(null);
  const preloadedAudio = usePreloadedAudio();

  function getNextAudioUrl(): string {
    if (!isStarted) {
      return words[roundState.completedWordIndex]?.wordAudioUrl ?? "";
    }

    if (roundState.completedWordIndex >= words.length - 1) {
      return "";
    }

    return words[roundState.completedWordIndex + 1].wordAudioUrl ?? "";
  }

  // Calculate scoring
  const {
    timeBonus: timeBonus,
    streakBonus,
    numCorrectSpellings,
    numIncorrectSpellings,
  } = useCurrentScore(words.length);

  const roundComplete = roundState.completedWordIndex > words.length - 1;
  const currentWord =
    words[roundState.completedWordIndex] || words[words.length - 1];

  const {
    animatePopup: animatePenalty,
    visibleNumber: visiblePenalty,
    showPopup: showPenalty,
  } = useAnimatedScorePopup();

  const {
    animatePopup: animateStreakGain,
    visibleNumber: visibleStreakGain,
    showPopup: showStreakGain,
  } = useAnimatedScorePopup();

  const {
    animatePopup: animateStreakLoss,
    visibleNumber: visibleStreakLoss,
    showPopup: showStreakLoss,
  } = useAnimatedScorePopup();

  // const { animatePopup: animateIncorrectPopup, showPopup: showIncorrectPopup } =
  //   useAnimatedScorePopup();

  // const {
  //   animatePopup: animatePartialCorrectPopup,
  //   showPopup: showPartialCorrectPopup,
  // } = useAnimatedScorePopup();

  // Run timer if needed
  const interval =
    isStarted && !roundComplete && !isPaused
      ? SPEED_SETTINGS.timerInterval
      : null;
  useInterval(() => {
    if (roundState.timeRemaining <= 0) {
      return;
    }
    const newTimeRemaining = Math.max(
      roundState.timeRemaining - SPEED_SETTINGS.timerInterval,
      0,
    );
    if (newTimeRemaining <= 0) {
      roundDispatch({ type: SpeedAction.TIMER_EXPIRED });
    } else {
      roundDispatch({
        type: SpeedAction.TIME_ELAPSED,
        payload: { time: newTimeRemaining },
      });
    }
  }, interval);

  const handleSpacePressed = useEffectEvent(() => {
    if (isStarted && !roundComplete) {
      setIsPaused(!isPaused);
    }
  });

  useKey(" ", handleSpacePressed);

  const handleEnterPressed = useEffectEvent(() => {
    if (!isStarted) {
      handleReadyButtonClick();
      return;
    }
  });
  useKey("Enter", handleEnterPressed);

  function playAudioForNextWord() {
    if (roundState.completedWordIndex < words.length - 1) {
      preloadedAudio.preload(getNextAudioUrl());
      wordCluesRef.current?.forcePlay(preloadedAudio);
    }
  }

  function handleReadyButtonClick() {
    setIsStarted(true);
    roundDispatch({
      type: SpeedAction.ROUND_STARTED,
      payload: {
        roundDuration: timerDuration,
      },
    });
    preloadedAudio.preload(getNextAudioUrl());
    wordCluesRef.current?.forcePlay(preloadedAudio);
  }

  function handleSkipButtonClick() {
    animatePenalty(SPEED_SETTINGS.skipPenalty);
    roundDispatch({ type: SpeedAction.SKIPPED });
    setCurrentStreak(0);
    animateStreakLoss(currentStreak);
    scoreDispatch({
      type: ScoreAction.WORD_SPELLED,
      payload: {
        roundIndex: roundIndex,
        userSpelling: "[skipped]",
        correctSpelling: currentWord.wordText.toLocaleLowerCase(),
      },
    });
    playAudioForNextWord();
  }

  function handlePauseButtonClick() {
    setIsPaused(true);
  }

  function handleResumeButtonClick() {
    setIsPaused(false);
  }

  function handleSpellingInputSubmit(word: string) {
    const userSpelling = word.toLocaleLowerCase();
    const correctSpelling = currentWord.wordText.toLocaleLowerCase();

    if (userSpelling === correctSpelling) {
      scoreDispatch({
        type: ScoreAction.WORD_SPELLED,
        payload: {
          roundIndex: roundIndex,
          userSpelling: userSpelling,
          correctSpelling: correctSpelling,
        },
      });

      // Calculate correct streak
      const newStreak = currentStreak + 1;
      setCurrentStreak(currentStreak + 1);
      animateStreakGain(1);
      if (newStreak > roundState.maxStreak) {
        roundDispatch({
          type: SpeedAction.NEW_MAX_STREAK,
          payload: { newStreak: newStreak },
        });
      }
      roundDispatch({ type: SpeedAction.ANSWERED_CORRECTLY });
    } else {
      const match = getWordCorrectness(userSpelling, correctSpelling);
      const isPartial = match >= GAME_SETTINGS.matchCorrectThreshold;
      const penalty = isPartial
        ? SPEED_SETTINGS.partiallyCorrectPenalty
        : SPEED_SETTINGS.incorrectPenalty;

      // if (!isPartial) {
      //   // Show 'almost' popup
      //   animatePartialCorrectPopup(-penalty);
      // } else {
      //   animateIncorrectPopup(-penalty);
      // }

      animateStreakLoss(currentStreak);
      setCurrentStreak(0);
      animatePenalty(penalty);

      if (isPartial) {
        roundDispatch({ type: SpeedAction.ANSWERED_PARTIALLY_CORRECT });
      } else {
        roundDispatch({ type: SpeedAction.ANSWERED_INCORRECTLY });
      }

      scoreDispatch({
        type: ScoreAction.WORD_SPELLED,
        payload: {
          roundIndex: roundIndex,
          userSpelling: userSpelling,
          correctSpelling: correctSpelling,
          pointValue: 0,
        },
      });
    }

    mixpanel.track("spelling_submitted", {
      game_id: gameData?.id,
      level_type: RoundType.SPEED,
      user_spelling: userSpelling,
      correct_spelling: correctSpelling,
    });

    playAudioForNextWord();
  }

  if (roundState.isOnIntro) {
    return (
      <RoundIntroScreen
        className="w-full h-full"
        roundType={RoundType.SPEED}
        roundIndex={roundIndex}
        onContinueClick={() => {
          roundDispatch({ type: SpeedAction.INTRO_CLOSED });
        }}
      />
    );
  }

  return (
    <div
      className={cn("w-full h-full", "flex flex-col items-center", className)}
    >
      <div className="w-full flex flex-col items-center gap-2 lg:gap-5 mb-2">
        <Header>{ROUND_TITLE_BY_ROUND_TYPE[RoundType.SPEED]}</Header>
        <h2 className={cn("font-semibold", "pt-0.5")}>Theme: {category}</h2>
        <WordSpellingRecord
          numTotalWords={words.length}
          completedSpellings={scoreState.roundScores[roundIndex]?.wordSpellings}
          className={cn(
            "grow min-h-0",
            windowHeight === WindowSize.SMALL && "max-h-48",
          )}
          hideScore={true}
        />
        <div // SCORE AREA
          className="w-full shrink flex flex-col gap-1"
        >
          <div className="flex justify-between px-1.5">
            <div // TIME BONUS HOLDER
              className={cn(
                "relative",
                "min-w-24",
                "flex align-baseline",
                "overflow-hidden",
              )}
            >
              <span>Time Bonus: {timeBonus.toString().padStart(2, "0")}</span>
              <span // SCORE PENALTY POPUP
                className={cn(
                  "absolute left-30",
                  showPenalty ? "opacity-100" : "opacity-0",
                  showPenalty ? "translate-x-0" : "translate-x-10",
                  "transition-all duration-300 ease-in-out",
                  "text-red-500",
                )}
              >
                -{visiblePenalty}
              </span>
            </div>
            <div // STREAK HOLDER
              className={cn(
                "relative",
                "min-w-19",
                "flex align-baseline",
                "overflow-hidden",
              )}
            >
              <span className={cn("flex-1", "flex justify-end")}>
                Streak: {currentStreak}
              </span>
              <span // STREAK GAIN POPUP
                className={cn(
                  "absolute left-20",
                  showStreakGain ? "opacity-100" : "opacity-0",
                  showStreakGain ? "translate-x-0" : "translate-x-10",
                  "transition-all duration-300 ease-in-out",
                  "text-green-600",
                )}
              >
                +{visibleStreakGain}
              </span>
              <span // STREAK PENALTY POPUP
                className={cn(
                  "absolute left-20",
                  showStreakLoss ? "opacity-100" : "opacity-0",
                  showStreakLoss ? "translate-x-0" : "translate-x-10",
                  "transition-all duration-300 ease-in-out",
                  "text-red-500",
                )}
              >
                -{visibleStreakLoss}
              </span>
            </div>
            {/* <p>
              Remaining:{" "}
              <span>
                {roundState.completedWordIndex}/{words.length}
              </span>
            </p> */}
          </div>
          <ProgressBar
            transitionClassName={"transition-all duration-100 ease-out"}
            value={
              (roundState.timeRemaining -
                numIncorrectSpellings * SPEED_SETTINGS.incorrectPenalty) /
              roundState.maxDuration
            }
          />
        </div>
        {!isStarted && (
          <div className="shrink flex-none">
            <MenuButton
              className={cn("mt-2", "min-w-20")}
              onClick={handleReadyButtonClick}
            >
              Ready
            </MenuButton>
          </div>
        )}
        {!roundComplete && (
          <div // BUTTONS
            className={cn(
              "my-1",
              "w-full shrink",
              "flex justify-center items-start gap-2",
              isStarted ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
          >
            <WordClues
              ref={wordCluesRef}
              wordAudioUrl={currentWord.wordAudioUrl}
              className="w-auto"
            />
            <MenuButton
              elevation={windowWidth === WindowSize.LARGE ? "lg" : "md"}
              className={
                windowWidth === WindowSize.LARGE ? "text-sm" : "text-xs"
              }
              onClick={() => {
                handleSkipButtonClick();
              }}
            >
              Skip (-{SPEED_SETTINGS.skipPenalty})
            </MenuButton>
            <MenuButton
              elevation={windowWidth === WindowSize.LARGE ? "lg" : "md"}
              className={
                windowWidth === WindowSize.LARGE ? "text-sm" : "text-xs"
              }
              onClick={handlePauseButtonClick}
            >
              Pause Timer
            </MenuButton>
          </div>
        )}
      </div>
      {isStarted && !roundComplete && (
        <div // INPUT
          className={cn(
            "relative",
            "w-full flex-1",
            "flex justify-center justify-self-start",
          )}
        >
          {/* <IncorrectPopup // POPUP FOR BEING COMPLETELY WRONG
              className={cn("absolute -top-10")}
              isVisible={showIncorrectPopup}
            >
              <span className={cn("text-red-600")}>Wrong!</span>
            </IncorrectPopup>
            <IncorrectPopup // POPUP FOR PARTIAL CORRECTNESS
              className={cn("absolute -top-10")}
              isVisible={showPartialCorrectPopup}
            >
              <span className={cn("text-yellow-600")}>Almost!</span>
            </IncorrectPopup> */}
          <KeyboardGroup
            className={cn("grow", inputType === "touch" && "justify-end")}
            onSubmit={handleSpellingInputSubmit}
            disableInput={isPaused}
          />
        </div>
      )}

      {roundComplete && (
        <div // GAME END AREA
          className={cn(
            "mt-4",
            "flex-1 flex flex-col justify-start items-center gap-4",
          )}
        >
          <div // SCORE PANEL
            className={cn(
              "flex flex-col items-start",
              "border border-black",
              "py-3 px-6",
              "gap-1",
            )}
          >
            <h2 className={cn("font-semibold")}>Final Score:</h2>
            <ul
              className={cn("flex flex-col items-start", "list-disc", "pl-5")}
            >
              <li>Time Bonus: {timeBonus}</li>
              <li>
                Words Spelled ({numCorrectSpellings}):{" "}
                {numCorrectSpellings * SPEED_SETTINGS.wordCorrectBonus}
              </li>
              <li>
                Max Streak ({roundState.maxStreak}): {streakBonus}
              </li>
            </ul>
            <hr className={cn("w-[90%]", "self-center", "mt-1")} />
            <p className={cn("font-semibold", "pl-5")}>
              Total:{" "}
              {timeBonus +
                numCorrectSpellings * SPEED_SETTINGS.wordCorrectBonus +
                streakBonus}
            </p>
          </div>
          <NextRoundButton
            className="mt-2"
            onClick={() => {
              scoreDispatch({
                type: ScoreAction.POINTS_EARNED,
                payload: {
                  roundIndex: roundIndex,
                  points:
                    timeBonus +
                    numCorrectSpellings * SPEED_SETTINGS.wordCorrectBonus +
                    streakBonus,
                },
              });
              roundDispatch({ type: SpeedAction.ROUND_COMPLETED });
              onComplete();
            }}
          />
        </div>
      )}
      {isPaused && <PauseOverlay onResumeClick={handleResumeButtonClick} />}
    </div>
  );
}

export default function WrappedSpeedRound(props: SpeedRoundProps) {
  return (
    <SpeedProvider key={props.roundIndex} roundIndex={props.roundIndex}>
      <SpeedRound {...props} />
    </SpeedProvider>
  );
}

// Keeping this here because even though I deactivated it, I think it would be
// cool to modularize it and use it thoughout the game if I have time.
// interface IncorrectPopupProps {
//   isVisible: boolean;
//   children?: React.ReactNode;
//   className?: string;
// }

// function IncorrectPopup({
//   isVisible,
//   children,
//   className,
// }: IncorrectPopupProps): React.ReactNode {
//   const animatedClasses = isVisible ? "opacity-100" : "opacity-0";

//   return (
//     <div
//       className={cn(
//         "p-1",
//         "transition-all",
//         "bg-white",
//         "border border-black",
//         "pointer-events-none",
//         animatedClasses,
//         className
//       )}
//     >
//       {children}
//     </div>
//   );
// }
